import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";

import { useCart } from "@shopify/hydrogen-react";

import { useCartActiveProduct } from "@cart/CartActiveProductContext";
import { useCartGiftWithPurchase } from "@cart/CartGiftWithPurchaseContext";
import { CartSpendingGitWithPurchaseSelection } from "@cart/CartSpendingGiftWithPurchase/CartSpendingGiftWithPurchaseSelection.data";
import { useLegacyCart } from "@cart/LegacyCartContext";
import { CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS } from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import {
  type CartLineWithAttributeInput,
  type CartSpendingBasedGiftWithPurchase,
  type CartSpendingBasedGiftWithPurchaseSettings,
  type TablescapeState,
} from "@tablescape/state";
import {
  findRelatedCartSpendingGiftWithPurchaseLine,
  getGiftWithPurchaseAttributeValue,
} from "@tablescape/utilities";

type Props = {
  giftWithPurchaseSettings:
    | CartSpendingBasedGiftWithPurchaseSettings[]
    | undefined;
};

const CartSpendingGitWithPurchaseManagerBase = ({
  giftWithPurchaseSettings,
}: Props) => {
  const { cost, lines, linesRemove } = useCart();
  const { updateToLegacyCart } = useLegacyCart();

  const { isProductUpdate } = useCartActiveProduct();
  const { setEligibleCartSpendingGiftWithPurchase } = useCartGiftWithPurchase();

  const {
    ui: { cartLineAdding, cartLineRemoving, cartLineUpdating },
  } = useSyncExternalStore<TablescapeState>(
    TablescapeContext.onAll,
    TablescapeContext.getSnapshot
  );

  const cartLineChanging = useMemo(
    () => cartLineAdding || cartLineRemoving || cartLineUpdating,
    [cartLineAdding, cartLineRemoving, cartLineUpdating]
  );

  const eligibleCartSpendingGiftWithPurchases = useMemo<
    CartSpendingBasedGiftWithPurchaseSettings[]
  >(() => {
    return cost?.subtotalAmount?.amount && giftWithPurchaseSettings
      ? giftWithPurchaseSettings.filter(
          (config: CartSpendingBasedGiftWithPurchaseSettings) =>
            config.pickYourOwnProductIds &&
            Number(cost?.subtotalAmount?.amount) >=
              Number(config.spendingThreshold)
        )
      : [];
  }, [cost?.subtotalAmount?.amount, giftWithPurchaseSettings]);

  useEffect(() => {
    if (cartLineChanging || isProductUpdate) return;

    const cartSpendingAmount = Number(cost?.subtotalAmount?.amount);

    const cartSpendingGiftLinesNoLongerEligible = lines
      .filter((line): line is CartLineWithAttributeInput => {
        if (!line || !line.id) return false;
        const cartSpendingGiftThreshold = getGiftWithPurchaseAttributeValue(
          line,
          CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD
        );
        return cartSpendingGiftThreshold
          ? Number(cartSpendingGiftThreshold) > cartSpendingAmount
          : false;
      })
      .map((line) => line.id);

    if (cartSpendingGiftLinesNoLongerEligible?.length > 0) {
      linesRemove(cartSpendingGiftLinesNoLongerEligible);
      updateToLegacyCart({
        updates: cartSpendingGiftLinesNoLongerEligible.map((id) => ({
          id,
          quantity: 0,
        })),
      });
    }
  }, [
    cartLineChanging,
    cost?.subtotalAmount?.amount,
    isProductUpdate,
    lines,
    linesRemove,
    updateToLegacyCart,
  ]);

  const checkIfGiftSelectionIsComplete = useCallback(
    (eligibleSpendingThreshold: string | undefined) => {
      return eligibleSpendingThreshold
        ? !!findRelatedCartSpendingGiftWithPurchaseLine(
            lines,
            eligibleSpendingThreshold
          )
        : false;
    },
    [lines]
  );

  const handleOpenGiftWithPurchaseDrawer = useCallback(
    (elibigleGiftWithPurchase: CartSpendingBasedGiftWithPurchase) => {
      setEligibleCartSpendingGiftWithPurchase(elibigleGiftWithPurchase);

      requestAnimationFrame(() => {
        TablescapeContext.emit(TablescapeEvent.TOGGLE_CART, {
          ui: {
            giftWithPurchaseDrawerOpen: true,
          },
        });
      });
    },
    [setEligibleCartSpendingGiftWithPurchase]
  );

  return eligibleCartSpendingGiftWithPurchases.map(
    (
      eligibleGiftWithPurchase: CartSpendingBasedGiftWithPurchaseSettings,
      index
    ) => (
      <CartSpendingGitWithPurchaseSelection
        cartLineChanging={cartLineChanging}
        checkIfGiftSelectionIsComplete={checkIfGiftSelectionIsComplete}
        giftWithPurchaseConfig={eligibleGiftWithPurchase}
        handleOpenGiftWithPurchaseDrawer={handleOpenGiftWithPurchaseDrawer}
        key={`${eligibleGiftWithPurchase.title}-${index}`}
      />
    )
  );
};

export const CartSpendingGitWithPurchaseManager = memo(
  CartSpendingGitWithPurchaseManagerBase
);
